import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

import { AuthService } from '../auth.service';
import { DynamicKeycloakService } from '../dynamic-keycloak.service';
import { KC_CONTEXT_KEY } from './auth.types';
import { getKeycloakContext } from './auth.utils';


@Injectable( { providedIn: 'root' } )
export class AuthGuard implements CanActivate {
  private authService = inject( AuthService );
  private dynamicKeycloakService = inject( DynamicKeycloakService );
  private router = inject( Router );

  async canActivate ( route: ActivatedRouteSnapshot, state: RouterStateSnapshot ): Promise<boolean | UrlTree> {
    if ( state.url.startsWith( '/signin/onboarding' ) ) {
      return true;
    }

    const keycloackContext = getKeycloakContext()

    if( keycloackContext && keycloackContext?.authProvider === 'keycloak' ) {
      const isLoggedIn = await this.dynamicKeycloakService.isLoggedIn();
      if ( isLoggedIn ) {
        if( keycloackContext?.shouldRegisterContact ) {
          await this.authService.registerContact();
        }
        const data = await this.authService.retrieveActorDetails();

        if ( data ) {
          // remove from session storage and store in localStorage
          sessionStorage.removeItem( KC_CONTEXT_KEY );

          // remove shouldRegisterContact and idp from keycloackContext
          const { shouldRegisterContact: _shouldRegisterContact, idp: _idp, ...updatedKeycloackContext } = keycloackContext;
          localStorage.setItem( KC_CONTEXT_KEY, JSON.stringify( updatedKeycloackContext ) );

          return this.redirectUserIfLoggedIn( keycloackContext.idp === 'magiclink' && keycloackContext.shouldRegisterContact )
        }
      }
    }


    const legacyValid = await this.authService.verifyUserToken();

    if ( legacyValid ) {
      return this.redirectUserIfLoggedIn()
    }

    return true
  }

  private redirectUserIfLoggedIn ( shouldOnboardContact?: boolean ): UrlTree {
    if( shouldOnboardContact ) {
      return this.router.createUrlTree( ['/signin/onboarding'] );
    }
    const route = this.authService.getPostAuthenticationRoute();
    this.authService.redirectUrl = null;
    return this.router.createUrlTree( [route] );
  }
}
